import { responseHandler } from './apiHelper';

/**
 * Deepcopies some data and assures that any bindings
 * from Formik or Redux are killed in the process.
 *
 * Note that this can only handle basic datastructures,
 * which can be parse as JSON.
 * */
const deepCopy = (data) => JSON.parse(JSON.stringify(data));

function preProcessClassified(classified) {
  const processed = deepCopy(classified);

  return processed;
}

/**
 * Dispatches POST request to save a classified.
 *
 * @param {object} classified Classified data on form
 *                      {isbn: string|number, salePoint: string, price: number, condition: string}
 * @return {Promise} Fetch response
 * */
// eslint-disable-next-line import/prefer-default-export
export const createClassified = (classified) => {
  const preProcessed = preProcessClassified(classified);
  const body = {
    ...preProcessed,
    isbn: parseInt(preProcessed.isbn, 10),
    price: parseInt(preProcessed.price, 10),
    tags: preProcessed.tags || [],
  };

  return fetch(
    '/api/v1/classifieds',
    {
      body: JSON.stringify(body),
      method: 'post',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )
    .then(responseHandler)
    .then((data) => {
      if (data.error) {
        throw new Error(data.error);
      }

      return data;
    });
};

/**
 * Retrieves the authenticated user's own classifieds from the Kotlin backend.
 *
 * @param {string|null} status Optional status filter (active|sold|inactive|outdated|deleted)
 * @return {Promise} Fetch response with a list of classifieds
 * */
export const listMyClassifieds = (status = null) => {
  const query = status ? `?status=${encodeURIComponent(status)}` : '';

  return fetch(
    `/api/v1/users/me/classifieds${query}`,
    {
      credentials: 'same-origin',
    },
  ).then(responseHandler);
};

/**
 * Updates a classified owned by the authenticated user.
 *
 * @param {number|string} id Classified id
 * @param {object} update Fields to update on the form
 *                      {condition, price, status, salePointId | salePoint: 'my_school'}
 * @return {Promise} Fetch response with the updated classified
 * */
export const updateClassified = (id, update) => fetch(
  `/api/v1/classifieds/${encodeURIComponent(id)}`,
  {
    method: 'put',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(update),
  },
)
  .then(responseHandler)
  .then((data) => {
    if (data.error) {
      throw new Error(data.error);
    }

    return data;
  });

/**
 * Soft-deletes a classified owned by the authenticated user.
 *
 * @param {number|string} id Classified id
 * @return {Promise} Fetch response with the deleted classified
 * */
export const deleteClassified = (id) => fetch(
  `/api/v1/classifieds/${encodeURIComponent(id)}`,
  {
    method: 'delete',
    credentials: 'same-origin',
  },
)
  .then(responseHandler);

/**
 * Retrieves a public seller's page (seller profile and active classifieds)
 * from the Kotlin backend.
 *
 * @param {number|string} userId Seller id
 * @param {number} page Pagination page
 * @param {number} limit Pagination limit
 * @return {Promise} Fetch response with the seller page
 * */
export const getSellerPage = (userId, page = 1, limit = 30) => fetch(
  `/api/v1/sellers/${encodeURIComponent(userId)}`
    + `?page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`,
  {
    credentials: 'same-origin',
  },
)
  .then(responseHandler);

export const listClassifiedsByIsbn = (isbn, location = 'All') => fetch(
  `/api/v1/books/${encodeURIComponent(isbn)}/classifieds?location=${encodeURIComponent(location)}`,
  {
    credentials: 'same-origin',
  },
)
  .then((data) => data.json());
